import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { FacebookPostPreviewType } from "../../../../shared/components/post-mockups/facebook-post-mock-up/facebook-post-mock-up.component";
import { CampaignPostContentType } from "../../../../shared/enums/campaignPost.enums";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCalendar } from "../../../../shared/models/brandCampaignCalendar";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandUrl } from "../../../brand.url";
import {
  BrandCalendarPostService,
  ICalendarPostAPIParameters,
} from "../../shared/services/brand-calendar-post.service";
import { BrandCampaignContentCalendarPostSelectionDialogData } from "./brand-campaign-content-calendar-post-selection-dialog-data";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import { BrandCampaignPost } from "../../../../shared/models/brandCampaignPost";
import { AdActionType } from "../../../../shared/enums/campaign.enums";
import {
  HOURS_24,
  MINUTES,
  TIMEZONES,
} from "../../../../shared/services/calendar.data";
import { NotificationService } from "../../../../shared/services/notification.service";
import { isHtmlElement } from "../../../../shared/utils/assert";

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
  selector: "app-brand-campaign-content-calendar-post-selection-dialog",
  templateUrl:
    "./brand-campaign-content-calendar-post-selection-dialog.component.html",
  styleUrl:
    "./brand-campaign-content-calendar-post-selection-dialog.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class BrandCampaignContentCalendarPostSelectionDialogComponent
  implements OnInit
{
  protected readonly FacebookPreviewType = FacebookPostPreviewType;
  protected readonly hours = HOURS_24;
  protected readonly minutes = MINUTES;
  protected readonly timezones = Object.keys(TIMEZONES);
  protected campaign: BrandCampaign;
  protected categories: BrandCampaignCategory[] = [];
  protected facebookPreviewType = FacebookPostPreviewType.Facebook;
  protected isCreateAdNotAllowed = false;
  protected isDarkPost = false;
  protected numberOfAdsCreated = 0;
  protected scheduledPublishDateHour: string;
  protected scheduledPublishDateMinute: string;
  protected scheduledPublishDateTimezone: string;
  protected selectedCategory?: BrandCampaignCategory;
  protected selectedIndex = 0;
  protected selecting = false;
  protected willBecomeAd = false;

  private readonly brandId: number;
  private campaignCalendar: BrandCampaignCalendar;
  private campaignDetails: BrandCampaignDetails;
  private selectedPublishDate: Date;

  constructor(
    private readonly brandCalendarPostService: BrandCalendarPostService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly dialogRef: MatDialogRef<BrandCampaignContentCalendarPostSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    data: BrandCampaignContentCalendarPostSelectionDialogData,
  ) {
    this.brandId = data.brandId;
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.campaignCalendar = data.campaignCalendar;
    this.selectedPublishDate = data.selectedDay;
    this.scheduledPublishDateHour = "10";
    this.scheduledPublishDateMinute = "00";
    this.scheduledPublishDateTimezone = dayjs.tz.guess();
  }

  public ngOnInit(): void {
    if (this.campaignDetails) {
      this.categories = this.campaignDetails.categories.filter(
        (category) => category.posts.length,
      );

      if (this.categories.length) {
        this.selectedCategory = this.categories[0];
      }

      setTimeout(() => {
        this.calculateNumberOfAdsCreated();
        this.setCurrentMediaPreview();
      }, 500);
    }
  }

  protected onSelectPost(): void {
    this.selecting = true;

    const selectedPost = this.getSelectedPost();
    const selectedPublishTimestamp = dayjs(this.selectedPublishDate)
      .hour(+this.scheduledPublishDateHour)
      .minute(+this.scheduledPublishDateMinute)
      .unix();
    const apiParams: ICalendarPostAPIParameters = {
      post_id: selectedPost?.id,
      suggested_publish_date: selectedPublishTimestamp,
      will_become_ad: selectedPost?.isGoogleAd ? true : this.willBecomeAd,
      scheduled_publish_date_timezone: this.scheduledPublishDateTimezone,
      is_dark_post: selectedPost?.isGoogleAd ? false : this.isDarkPost,
    };

    this.brandCalendarPostService
      .addBrandCalendarPost(
        this.campaign.id,
        this.campaignCalendar.id,
        apiParams,
      )
      .subscribe({
        next: (calendarPost) => {
          this.selecting = false;
          this.calculateNumberOfAdsCreated();
          this.dialogRef.close(calendarPost);
        },
        error: () => {
          this.selecting = false;
          this.notificationService.error(
            "brand.campaign.contentCalendar.errorUpdatingThePost",
          );
        },
      });
  }

  protected calculateNumberOfAdsCreated(): void {
    this.numberOfAdsCreated = 0;

    if (!this.campaign?.currentCalendar) {
      return;
    }

    this.campaign.currentCalendar.calendarPosts.forEach((post) => {
      if (post.isGoogleAd) {
        this.numberOfAdsCreated++;
      } else if (post.willBecomeAd) {
        this.numberOfAdsCreated++;
      }
    });

    if (this.willBecomeAd) {
      this.numberOfAdsCreated++;
    }
  }

  protected hasReachedMaxNumberOfAds(): boolean {
    return (
      !this.willBecomeAd &&
      this.campaign.promoAdsEnabled &&
      this.numberOfAdsCreated >= this.campaign.maxAdsCampaignPartner
    );
  }

  protected checkLink(event: MouseEvent): void {
    if (
      isHtmlElement(event.target) &&
      event.target.classList.contains("can-not-save-post-warning--link")
    ) {
      this.goToGoogleAds();
    }
  }

  private goToGoogleAds(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(
      BrandUrl.Campaign.Ads(this.brandId, this.campaign.id),
    );
  }

  protected canSave(): boolean {
    if (this.selecting) {
      return false;
    } else if (this.getSelectedPost()?.isGoogleAd) {
      if (this.hasReachedMaxNumberOfAds()) {
        return false;
      }

      return !this.canNotCreateGoogleAdWarning();
    }

    return true;
  }

  protected canNotCreateGoogleAdWarning(): boolean {
    return Boolean(
      !this.campaign.useGoogleAds &&
        this.selectedCategory?.posts &&
        this.getSelectedPost()?.isGoogleAd,
    );
  }

  protected getSelectedPost(): BrandCampaignPost | undefined {
    if (
      this.selectedCategory?.posts.length &&
      this.selectedCategory?.posts[this.selectedIndex]
    ) {
      return this.selectedCategory.posts[this.selectedIndex];
    }

    return undefined;
  }

  protected onCategoryChanged(): void {
    this.selectedIndex = 0;
  }

  protected canCreateDarkPost(): boolean {
    return this.campaign?.adActionType !== AdActionType.Boost;
  }

  protected toggleFacebookPreviewType(): void {
    this.facebookPreviewType =
      this.facebookPreviewType === FacebookPostPreviewType.Facebook
        ? FacebookPostPreviewType.Instagram
        : FacebookPostPreviewType.Facebook;
  }

  protected showFacebookPreview(post: BrandCampaignFacebookPost): boolean {
    if (!post.postToInstagram) {
      return true;
    }

    return this.facebookPreviewType === this.FacebookPreviewType.Facebook;
  }

  protected showInstagramPreview(post: BrandCampaignFacebookPost): boolean {
    if (this.showFacebookPreview(post)) {
      return false;
    }

    return this.facebookPreviewType === this.FacebookPreviewType.Instagram;
  }

  protected showMediaPreviewButton(): boolean {
    const selectedPost = this.getSelectedPost();

    if (!selectedPost) {
      return false;
    }

    if (selectedPost instanceof BrandCampaignFacebookPost) {
      return selectedPost.postToFacebook && selectedPost.postToInstagram;
    }

    return false;
  }

  protected slideChanged(event: number): void {
    this.selectedIndex = event;
    this.setCurrentMediaPreview();
  }

  private setCurrentMediaPreview() {
    const post = this.getSelectedPost();

    if (post instanceof BrandCampaignFacebookPost) {
      this.facebookPreviewType = post.postToFacebook
        ? FacebookPostPreviewType.Facebook
        : FacebookPostPreviewType.Instagram;
    }

    if (this.campaign.adActionType === AdActionType.Boost) {
      this.isCreateAdNotAllowed = Boolean(
        post?.mediaType === CampaignPostContentType.Gif ||
          post?.mediaType === CampaignPostContentType.Image360,
      );
    }
  }
}
